import { message } from "antd";

import React, { useState } from "react";

import { Input } from "../ui/input.tsx";
import { Button } from "../ui/button.tsx";
import { useApi } from "../../Api/useApi.ts";
import { Textarea } from "../ui/textarea.tsx";
import { Sheet, SheetContent } from "../ui/sheet.tsx";
import { newFormProps } from "../../Constants/newForm.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Plus, Sparkles, LoaderCircle } from "lucide-react";
import { useFormEditorContext } from "../../Contexts/FormEditorContext.jsx";
import { convertFormObject } from "../../Components/MyEditor/FormObjectConfig.jsx";

type FormType = {
    icon: React.ReactNode;
    title: string;
    description: string;
    type: "dynamic" | "static";
};

const formTypes: FormType[] = [
    {
        icon: <Sparkles className="size-[20px]" />,
        title: "Create with AI",
        description: "Generate your tailored form with AI in seconds",
        type: "dynamic",
    },
    {
        icon: <Plus className="size-[20px]" />,
        title: "Start From Scratch",
        description: "Build your form from scratch",
        type: "static",
    },
];

export function MobileFormCreateSheet({}) {
    const [selectedFormType, setSelectedFormType] = useState<
        "dynamic" | "static" | null
    >(null);

    const [openSheet, setOpenSheet] = useState(false);

    return (
        <Sheet open={openSheet} onOpenChange={setOpenSheet}>
            <Button
                onClick={() => {
                    setOpenSheet(true);
                    setSelectedFormType(null);
                }}
                className="w-full h-10 py-3 text-white bg-gray-900 rounded-lg hover:bg-gray-800"
            >
                <Plus className="w-4 h-4 mr-2" />
                New Form
            </Button>

            <SheetContent
                className="p-3 rounded-t-[12px] "
                showOverlay
                side={"bottom"}
            >
                <h2 className="text-base font-semibold ">Create a New Form</h2>
                {!selectedFormType && (
                    <div className="flex flex-col w-full gap-3 p-4">
                        {formTypes.map((formType, index) => (
                            <FormTypeCard
                                key={index}
                                icon={formType.icon}
                                title={formType.title}
                                description={formType.description}
                                type={formType.type}
                                setSelectedFormType={setSelectedFormType}
                            />
                        ))}
                    </div>
                )}
                {selectedFormType === "dynamic" && (
                    <DynamicForm
                        setSheetOpen={setOpenSheet}
                        setSelectedFormType={setSelectedFormType}
                    />
                )}
                {selectedFormType === "static" && (
                    <StaticForm
                        setSheetOpen={setOpenSheet}
                        setSelectedFormType={setSelectedFormType}
                    />
                )}
            </SheetContent>
        </Sheet>
    );
}

function FormTypeCard({
    key,
    icon,
    title,
    description,
    type,
    setSelectedFormType,
}) {
    return (
        <div
            key={key}
            className="flex flex-col rounded-[12px] h-[120px] w-full border border-[#DCDFE4] items-center justify-center gap-2 "
            onClick={() => setSelectedFormType(type)}
        >
            {icon}

            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="text-xs text-center text-gray-500">{description}</p>
        </div>
    );
}

/* Dynamic form */
type DynamicFormCreateInput = {
    name: string;
    description: string;
    workspaceId: any;
};

function DynamicForm({ setSelectedFormType, setSheetOpen }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [error, setError] = useState("");

    const [dynamicName, setDynamicName] = useState("");
    const { selectedWorkSpace } = useFormEditorContext();
    const [dynamicDescription, setDynamicDescription] = useState("");
    const [isCreatingDynamicForm, setIsCreatingDynamicForm] = useState(false);

    const dynamicFormCreateMutation = useMutation({
        mutationFn: ({
            name,
            description,
            workspaceId,
        }: DynamicFormCreateInput) => {
            // Ensure createAiForms returns a promise
            return new Promise((resolve, reject) => {
                api.aiForms
                    .createAiForms(name, description, workspaceId)
                    .then((response) => {
                        resolve(response);
                        //console.log(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        onSuccess: (data) => {
            setIsCreatingDynamicForm(false);
            queryClient.invalidateQueries({
                queryKey: ["formList"],
                exact: false,
            });
            setSheetOpen(false);
            setSelectedFormType(null);
        },
        onError: (error) => {
            console.error("Mutation error:", error);
            setIsCreatingDynamicForm(false);
        },
    });
    const handleDynamicFormCreation = () => {
        if (!dynamicName) {
            setError("Fields can't be empty");
            return;
        }
        if (!selectedWorkSpace) {
            message.error("Please select a workspace");
            return;
        }

        setIsCreatingDynamicForm(true);
        dynamicFormCreateMutation.mutate({
            name: dynamicName,
            description: dynamicDescription,
            workspaceId: selectedWorkSpace,
        });
    };

    return (
        <>
            <div className="mt-2 mb-4">
                <div className="rounded-[8px] w-full bg-blue-50 flex p-2 gap-1.5 items-start">
                    <svg
                        width="14"
                        height="15"
                        viewBox="0 0 14 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M7.00008 9.64616V7.31283M7.00008 4.97949H7.00591M12.8334 7.31283C12.8334 10.5345 10.2217 13.1462 7.00008 13.1462C3.77842 13.1462 1.16675 10.5345 1.16675 7.31283C1.16675 4.09116 3.77842 1.47949 7.00008 1.47949C10.2217 1.47949 12.8334 4.09116 12.8334 7.31283Z"
                            stroke="#2563EB"
                            stroke-width="0.875"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>

                    <p className="text-xs text-blue-600">
                        For better results provide clear details, audience, and
                        context
                    </p>
                </div>
            </div>
            <div className="flex flex-col gap-4 mb-4">
                <div className="flex flex-col">
                    <p className="text-xs font-medium text-primary">
                        Form name
                    </p>
                    <p className="text-xs text-zinc-500">
                        The form name will be displayed when sharing
                    </p>
                    <Input
                        className="flex w-full h-10 px-3 py-2 mt-1 text-xs transition duration-150 bg-white border rounded-lg border-zinc-200 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-zinc-500"
                        required
                        placeholder="e.g. Feedback form"
                        onChange={(e) => {
                            setError("");
                            setDynamicName(e.target.value);
                        }}
                        autoFocus
                    />
                </div>

                <div className="flex flex-col">
                    <p className="text-xs font-medium text-primary">
                        Form description
                    </p>
                    <p className="text-xs text-zinc-500">
                        Describe the goal of this form
                    </p>
                    <Textarea
                        rows={1}
                        className="flex w-full h-10 px-3 py-2 mt-1 text-xs transition duration-150 bg-white border rounded-lg resize-none border-zinc-200 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-zinc-500 placeholder:text-xs"
                        placeholder="e.g. A form to collect feedback from my customers"
                        onChange={(e) => {
                            setError("");
                            setDynamicDescription(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleDynamicFormCreation();
                            }
                        }}
                    />
                    {error && (
                        <p className="pl-1 mt-1 text-xs text-red-500">
                            {error}
                        </p>
                    )}
                </div>
            </div>
            {/* button section */}
            <div className="flex flex-col gap-2 mb-4">
                <Button
                    className="h-9 text-white rounded-[8px] py-1.5 bg-primary hover:bg-primary/90"
                    onClick={handleDynamicFormCreation}
                    type="submit"
                    disabled={isCreatingDynamicForm}
                >
                    {isCreatingDynamicForm ? (
                        <div className="flex gap-1.5 items-center">
                            <span className="bg-white rounded-full size-3"></span>
                            <span className="text-sm">
                                Buildform AI is thinking...
                            </span>
                        </div>
                    ) : (
                        "          Build a New Form"
                    )}
                </Button>
                <CommonButtonArea
                    setSelectedFormType={setSelectedFormType}
                    setSheetOpen={setSheetOpen}
                />
            </div>
        </>
    );
}

type StaticFormCreateInput = {
    data: any;
    workspaceId: any;
};

function StaticForm({ setSelectedFormType, setSheetOpen }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [error, setError] = useState("");
    const [guidedName, setGuidedName] = useState("");
    const [isCreatingGeneralForm, setIsCreatingGeneralForm] = useState(false);

    const { selectedWorkSpace } = useFormEditorContext();

    const formCreateMutation = useMutation({
        mutationFn: ({ data, workspaceId }: StaticFormCreateInput) =>
            // @ts-ignore
            api.revisions.createRevision(data, workspaceId),
        onSuccess: (data) => {
            setIsCreatingGeneralForm(false);
            queryClient.invalidateQueries({
                queryKey: ["formList"],
                exact: false,
            });
            setSheetOpen(false);
            setSelectedFormType(null);
        },
        onError: (error) => {
            //@ts-ignore
            if (error.response) {
                //@ts-ignore
                //console.log(error?.response);
                message.error(
                    //@ts-ignore
                    error?.response?.data?.detail || "Something went wrong",
                );
            } else {
            }
            console.error("Error details:", error);
            setIsCreatingGeneralForm(false);
        },
    });

    const handleCreateNewForm = () => {
        if (!guidedName) {
            setError("Fields can't be empty");
            return;
        }
        if (!selectedWorkSpace) {
            message.error("Please select a workspace");
            return;
        }
        setIsCreatingGeneralForm(true);
        const newForm = newFormProps(guidedName);
        const newFormat = convertFormObject(newForm);
        formCreateMutation.mutate({
            data: newFormat,
            workspaceId: selectedWorkSpace,
        });
    };
    return (
        <>
            <div className="mt-2 mb-4">
                <div className="rounded-[8px] w-full bg-blue-50 flex p-2 gap-1.5 items-start">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        data-slot="icon"
                        className="h-[0.8rem] min-w-[0.8rem] text-blue-600 mt-0.5"
                    >
                        <path d="M10 1a6 6 0 0 0-3.815 10.631C7.237 12.5 8 13.443 8 14.456v.644a.75.75 0 0 0 .572.729 6.016 6.016 0 0 0 2.856 0A.75.75 0 0 0 12 15.1v-.644c0-1.013.762-1.957 1.815-2.825A6 6 0 0 0 10 1ZM8.863 17.414a.75.75 0 0 0-.226 1.483 9.066 9.066 0 0 0 2.726 0 .75.75 0 0 0-.226-1.483 7.553 7.553 0 0 1-2.274 0Z"></path>
                    </svg>
                    <p className="text-xs text-blue-600">
                        AI statements are available for Start from Scratch
                        Projects
                    </p>
                </div>
            </div>
            <div className="flex flex-col gap-4 mb-4">
                <div className="flex flex-col">
                    <p className="text-xs font-medium text-primary">
                        Form name
                    </p>
                    <p className="text-xs text-zinc-500">
                        The form name will be displayed when sharing
                    </p>
                    <Input
                        className="flex w-full h-10 px-3 py-2 mt-1 text-xs transition duration-150 bg-white border rounded-lg border-zinc-200 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-zinc-500"
                        required
                        placeholder="e.g. Feedback form"
                        onChange={(e) => {
                            setError("");
                            setGuidedName(e.target.value);
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleCreateNewForm();
                            }
                        }}
                    />
                    {error && (
                        <p className="pl-1 mt-1 text-xs text-red-500">
                            {error}
                        </p>
                    )}
                </div>
            </div>
            {/* button section */}
            <div className="flex flex-col gap-3 ">
                <Button
                    className="h-9  text-white rounded-[8px] py-1.5 bg-primary hover:bg-primary/90"
                    onClick={handleCreateNewForm}
                    disabled={isCreatingGeneralForm}
                >
                    {isCreatingGeneralForm ? (
                        <LoaderCircle className="animate-spin" />
                    ) : (
                        <span>Build a New Form</span>
                    )}
                </Button>
                <CommonButtonArea
                    setSelectedFormType={setSelectedFormType}
                    setSheetOpen={setSheetOpen}
                />
            </div>
        </>
    );
}

function CommonButtonArea({ setSelectedFormType, setSheetOpen }) {
    return (
        <div className="flex items-center gap-2 ">
            <Button
                variant={"outline"}
                className=" h-9"
                onClick={() => {
                    setSelectedFormType(null);
                }}
            >
                <ChevronLeft className="size-4" />
            </Button>
            <Button
                className="bg-white h-9 w-full rounded-[8px] font-medium  text-primary border-[#DCDFE4] border hover:bg-white"
                onClick={() => {
                    setSelectedFormType(null);
                    setSheetOpen(false);
                }}
            >
                Cancel
            </Button>
        </div>
    );
}
